import React, { useState, useRef, useEffect } from 'react';
import { Search, ArrowLeft, AlertTriangle, User } from 'lucide-react';
import { directOrderService } from '../../../../services/directOrderService';
import { CustomerSearchDto, CustomerDetailDto } from '../../../../types/directOrder';

interface CustomerSearchViewProps {
  onCustomerSelected: (customer: CustomerDetailDto) => void;
  onBack?: () => void;
}

export const CustomerSearchView: React.FC<CustomerSearchViewProps> = ({
  onCustomerSelected,
  onBack
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CustomerSearchDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setError(null);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (term.length < 2) {
      setResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await directOrderService.searchCustomers(term);
        setResults(data);
      } catch (err: any) {
        setError(err.message || 'Søk feilet');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelectCustomer = async (customerNo: string) => {
    try {
      setLoadingCustomer(customerNo);
      setError(null);
      const detail = await directOrderService.getCustomer(customerNo);

      if (detail.creditBlocked) {
        setError(`Kunde ${customerNo} (${detail.customerName}) er kredittsperret og kan ikke benyttes.`);
        setLoadingCustomer(null);
        return;
      }

      onCustomerSelected(detail);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke hente kunde');
    } finally {
      setLoadingCustomer(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-white">
        {onBack && (
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <h1 className="text-lg font-semibold">Direkteordre - Velg kunde</h1>
      </div>

      {/* Search */}
      <div className="p-4 bg-white border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Søk etter kunde (navn, nummer, adresse...)"
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && (
          <div className="text-center py-8 text-gray-500">Søker...</div>
        )}

        {!loading && searchTerm.length >= 2 && results.length === 0 && (
          <div className="text-center py-8 text-gray-500">Ingen kunder funnet</div>
        )}

        {!loading && searchTerm.length < 2 && (
          <div className="text-center py-12 text-gray-400">
            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Skriv minst 2 tegn for å søke</p>
          </div>
        )}

        <div className="space-y-2">
          {results.map((customer) => (
            <button
              key={customer.customerNo}
              onClick={() => handleSelectCustomer(customer.customerNo)}
              disabled={loadingCustomer === customer.customerNo || customer.creditBlocked}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                customer.creditBlocked
                  ? 'bg-red-50 border-red-200 opacity-60 cursor-not-allowed'
                  : 'bg-white border-gray-200 hover:bg-blue-50 hover:border-blue-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-gray-900">
                    {customer.customerNo} - {customer.customerName}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {[customer.delAddress1, customer.delZipCode, customer.delCity]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
                {customer.creditBlocked && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                    Kredittsperret
                  </span>
                )}
                {loadingCustomer === customer.customerNo && (
                  <span className="text-sm text-blue-600">Laster...</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
